/**
 * V77 — Makeover Leads Admin Panel
 *
 * Admin-only page at /admin/makeover-leads gated by ProtectedAdminRoute.
 * Displays enquiries submitted via /online-makeover-classes* pages, with
 * search / country-filter / status-filter and CSV export. Completely
 * independent from the existing tuition-lead management surface.
 */
import { Fragment, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Search, Download, RefreshCw, Loader2, MapPin, Mail, MessageCircle, User, Clock } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

// Prefer the V175 array; fall back to splitting the legacy string.
const leadServices = (r) => (r.preferred_services?.length
    ? r.preferred_services
    : (r.preferred_service || "").split(",").map((s) => s.trim()).filter(Boolean));

export default function MakeoverLeadsPanel() {
    const navigate = useNavigate();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [countryFilter, setCountryFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [expanded, setExpanded] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q.trim()) params.set("q", q.trim());
            if (countryFilter) params.set("country", countryFilter);
            if (statusFilter) params.set("status", statusFilter);
            params.set("limit", "200");
            const res = await api.get(`/admin/makeover/leads?${params.toString()}`);
            setRows(res.data?.items || []);
            setTotal(res.data?.total || 0);
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed to load leads.");
        } finally {
            setLoading(false);
        }
    }, [q, countryFilter, statusFilter]);

    useEffect(() => { load(); }, [load]);

    async function exportCsv() {
        try {
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/makeover/leads/export.csv`;
            const token = localStorage.getItem("at_token");
            const superToken = sessionStorage.getItem("at_super_mode_token");
            const res = await fetch(url, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    ...(superToken ? { "X-Super-Mode-Token": superToken } : {}),
                },
            });
            if (!res.ok) {
                toast.error("Export failed.");
                return;
            }
            const blob = await res.blob();
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `makeover_leads_${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("CSV downloaded.");
        } catch {
            toast.error("Export failed.");
        }
    }

    // Distinct country list derived from data
    const countryOptions = Array.from(new Set(rows.map((r) => r.country).filter(Boolean))).sort();

    return (
        <div className="min-h-screen bg-slate-50" data-testid="makeover-leads-panel">
            <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 flex-wrap">
                    <button onClick={() => navigate("/admin/dashboard")} data-testid="makeover-back" className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
                        <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    <div className="h-4 w-px bg-slate-200" />
                    <h1 className="font-heading font-bold text-lg text-slate-900">Makeover Class Leads</h1>
                    <span className="text-[11px] uppercase tracking-wider font-bold text-rose-600">V77 · New Service</span>
                    <div className="ml-auto flex items-center gap-2">
                        <button onClick={load} disabled={loading} data-testid="makeover-refresh" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-100 disabled:opacity-50">
                            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                            Refresh
                        </button>
                        <button onClick={exportCsv} data-testid="makeover-export" className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg bg-slate-900 text-white hover:bg-slate-700">
                            <Download className="w-3.5 h-3.5" /> Export CSV
                        </button>
                    </div>
                </div>
            </div>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-5 space-y-4">
                <div className="bg-white border border-slate-200 rounded-2xl p-4 grid sm:grid-cols-4 gap-3">
                    <div className="relative sm:col-span-2">
                        <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search name / email / WhatsApp / city…"
                            data-testid="makeover-search"
                            className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none"
                        />
                    </div>
                    <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)} data-testid="makeover-country-filter" className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                        <option value="">All countries</option>
                        {countryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="makeover-status-filter" className="px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white">
                        <option value="">All statuses</option>
                        <option value="new">new</option>
                        <option value="contacted">contacted</option>
                        <option value="scheduled">scheduled</option>
                        <option value="closed">closed</option>
                    </select>
                </div>

                <div className="text-xs text-slate-500">Showing {rows.length} of {total} leads</div>

                <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
                                    <th className="py-2.5 px-3">Received</th>
                                    <th className="py-2.5 px-3">Name</th>
                                    <th className="py-2.5 px-3">Location</th>
                                    <th className="py-2.5 px-3">Contact</th>
                                    <th className="py-2.5 px-3">Preferred Service</th>
                                    <th className="py-2.5 px-3">Time zone</th>
                                    <th className="py-2.5 px-3">Status</th>
                                </tr>
                            </thead>
                            <tbody data-testid="makeover-leads-tbody">
                                {rows.length === 0 && !loading && (
                                    <tr><td colSpan={7} className="text-center py-10 text-slate-500 text-sm">No enquiries yet.</td></tr>
                                )}
                                {rows.map((r) => (
                                    <Fragment key={r.id}>
                                        <tr className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                                            <td className="py-2 px-3 text-slate-500 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                                            <td className="py-2 px-3 font-semibold text-slate-900"><User className="inline w-3.5 h-3.5 mr-1 text-slate-400" />{r.name}<span className="text-[10px] text-slate-500 ml-1.5">· {r.gender} · {r.age}</span></td>
                                            <td className="py-2 px-3 text-slate-700"><MapPin className="inline w-3.5 h-3.5 mr-1 text-slate-400" />{[r.city, r.country].filter(Boolean).join(", ")}{r.country_code && <span className="ml-1 text-[10px] text-slate-500 uppercase">({r.country_code})</span>}</td>
                                            <td className="py-2 px-3 text-xs text-slate-600 space-y-0.5">
                                                <div><Mail className="inline w-3 h-3 mr-1" /><a href={`mailto:${r.email}`} className="hover:text-slate-900">{r.email}</a></div>
                                                <div>
                                                    <MessageCircle className="inline w-3 h-3 mr-1" />
                                                    <a href={`https://wa.me/${(r.whatsapp || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noopener noreferrer" className="hover:text-slate-900">{r.whatsapp_full || r.whatsapp}</a>
                                                </div>
                                            </td>
                                            <td className="py-2 px-3 text-slate-700 text-xs" data-testid={`makeover-lead-services-${r.id}`}>
                                                <div className="flex flex-wrap gap-1 max-w-[260px]">
                                                    {leadServices(r).map((s) => (
                                                        <span key={s} className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-100">{s}</span>
                                                    ))}
                                                </div>
                                            </td>
                                            <td className="py-2 px-3 text-xs text-slate-600">
                                                <Clock className="inline w-3 h-3 mr-1" />{r.timezone}
                                                {r.local_time_at_submission && <div className="text-[10px] text-slate-500">Submitted at {r.local_time_at_submission}</div>}
                                                <div className="text-[10px] text-slate-500">{r.preferred_time}</div>
                                            </td>
                                            <td className="py-2 px-3"><span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-800">{r.status}</span></td>
                                        </tr>
                                        {expanded === r.id && (
                                            <tr className="bg-slate-50 border-b border-slate-100">
                                                <td colSpan={7} className="py-3 px-3 text-xs text-slate-700 space-y-2">
                                                    {r.whatsapp_country_code && (
                                                        <div className="grid sm:grid-cols-3 gap-3 pb-2 border-b border-slate-200">
                                                            <div><strong className="text-slate-900">WhatsApp Country Code:</strong> <span className="font-mono">{r.whatsapp_country_code}</span></div>
                                                            <div><strong className="text-slate-900">Full International:</strong> <span className="font-mono">{r.whatsapp_full || r.whatsapp}</span></div>
                                                            <div><strong className="text-slate-900">Local Time at Submission:</strong> {r.local_time_at_submission || "—"}</div>
                                                        </div>
                                                    )}
                                                    {r.goals && <div><strong>Goals:</strong> {r.goals}</div>}
                                                    {r.notes && <div><strong>Notes:</strong> {r.notes}</div>}
                                                    {r.source && <div className="text-slate-500"><strong>Source:</strong> {r.source}</div>}
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
        </div>
    );
}
